import i18n from '@assets/i18n';
import {
    DeviceType,
    Experience,
    ExperienceVersion,
    ExperienceCreativeTerminal
} from '@models/experience/experience';
import baseConfig from '@config/base.config';
import uuidUtils from '@utils/uuid.utils';
import { v4 as uuidv4 } from 'uuid';
import cookieUtils from '@utils/cookie.utils';
import permissionUtils from '@utils/permission.utils';
import { usePackageInfo } from '@models/package/usePackageInfo';
import { useAccountInfo } from '@models/account/useAccountInfo';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import { usePermission } from '@models/profile/useProfilePermission';
import usePlanLimit from '@master/hooks/service/usePlanLimit/usePlanLimit';

const { account } = useAccountInfo();
const { profileInfo } = useProfileInfo();
const { packageInfo } = usePackageInfo();
const { permissions } = usePermission();
const { getPtxOldPackageInfo } = usePlanLimit();
const canPublishTemplate = permissionUtils.includePermissionWithAccountInfo(['popupTemplate'], {
    permissionIdList: permissions.value?.permissionIdList || [],
    permissionDict: permissions.value?.permissionDict || []
});
const token =
    cookieUtils.getItem(baseConfig.ACCOUNT_TOKEN) || localStorage.getItem(baseConfig.ACCOUNT_TOKEN);

export interface EditorData {
    visible: boolean;
    activeType?: 'edit' | 'create';
    editorType?: 'popup' | 'inline';
    baseUrl?: string;
    versionId?: string;
    terminalId?: string;
    templateId?: string;
}

export function loadEditor(editorIframe: HTMLIFrameElement) {
    const host = import.meta.env.VITE_POPUP_EDITOR_URL;
    const clarityString = `<!-- Clarity Tracking Code for Ptengine Site -->
        <script>
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${import.meta.env.VITE_CLARITY_TOKEN}");
        </script>`;
    const iframeContent = `\<!DOCTYPE html\>
        \<html style="background-color:transparent" lang="zh-CN"\>
            \<head\>
                \<title\>Ptengine Experience Popup Editor</title\>
                \<link rel="stylesheet" type="text/css" href="${host}/editor.css"\>
                \<link rel="stylesheet" href="${host}/static/vendor/froala-editor/styles.css" /\>
                \<script src="//at.alicdn.com/t/font_2001444_z26r92atq4e.js"\>\</script\>
                \<!-- ptengine-icon --\>
                \<script src="//at.alicdn.com/t/font_2680239_2oz3tssz9ri.js"\>\</script\>
                \<script src="${host}/static/vendor/froala-editor/froala_editor.pkgd.min.js"\>\</script\>
                \<script src="${host}/static/vendor/froala-editor/lx_align.js"\>\</script\>
                \<script src="${host}/static/vendor/froala-editor/lx_color.js"\>\</script\>
                \<script src="${host}/static/vendor/froala-editor/lx_font_family.js"\>\</script\>
                \<script src="${host}/static/vendor/froala-editor/lx_lists.js"\>\</script\>
                \<script src="${host}/static/vendor/froala-editor/zh_cn.js"\>\</script\>
                \<script src="${host}/static/vendor/froala-editor/ja.js"\>\</script\>
                \<script src="${host}/static/vendor/froala-editor/styles.js"\>\</script\>
                ${clarityString}
            \</head>
            \<body style="background-color:transparent"\>
                \<div id="app">
                \</div>
                \<script src="${host}/vendor.dll.js"\>\</script\>
                \<script src="${host}/editor.js"\>\</script\>
            \</body>
        \</html>`;
    const contentDocument = editorIframe.contentDocument || editorIframe.contentWindow?.document;
    contentDocument &&
        (contentDocument.open(), contentDocument.write(iframeContent), contentDocument.close());
}

export function getNewVersion(experienceId: string): ExperienceVersion {
    const id = uuidv4() + '';
    const version: ExperienceVersion = {
        id,
        engageID: experienceId,
        name: i18n.global.t('experience.setting.version_default_name'),
        rate: 1,
        controlGroup: false,
        screenshot: '',
        terminals: [getTerminal(1, id), getTerminal(2, id)]
    };
    return version;
}

export function getControlGroup(experienceId: string): ExperienceVersion {
    const id = uuidv4() + '';
    const version: ExperienceVersion = {
        id,
        engageID: experienceId,
        name: i18n.global.t('experience.setting.control_group'),
        rate: 0,
        controlGroup: true,
        screenshot: '',
        terminals: []
    };
    return version;
}

export function getTerminal(
    deviceType: DeviceType,
    versionId: string,
    templateType?: 'popup' | 'stickybar',
    template?: never
): ExperienceCreativeTerminal {
    const terminal = {
        id: uuidv4() + '',
        type: deviceType,
        status: 1,
        popup: [],
        stickybar: [],
        code: [],
        inline: [],
        engageVersionID: versionId
    };
    templateType && template && terminal[templateType].push(template);
    return terminal;
}

export function getNewPopup(
    type: 'popup' | 'stickybar',
    position: string,
    namePrefix: string,
    settingVal: any
) {
    const id = `${type}_${uuidUtils.uuid_8bit()}`;
    const widgetSetting = [
        {
            widgetId: `pt_${uuidUtils.uuid_8bit()}`,
            widgetSettings: {
                ...settingVal,
                position,
                showMask: type === 'popup'
            },
            widgetNodes: {
                html: '',
                css: ''
            },
            widgetHasForm: false,
            widgetHasError: false
        }
    ];
    const layout = {
        popupId: id,
        popupName: (namePrefix || '') + ' 1',
        popupHasError: false,
        popupHasForm: false,
        widgetLayout: '',
        widgets: widgetSetting,
        trigger: {
            start: [],
            stop: []
        }
    };
    return [layout];
}

// 默认新增优先按其他版本设备同步，默认不开启移动端
export function getDefaultDeviceStatus(experience: Experience) {
    const firstVersion = experience.versions.filter(v => !v.controlGroup)?.[0];
    const spStatus = firstVersion?.terminals?.find(t => t.type === 1)?.status ?? 0;
    const pcStatus = firstVersion?.terminals?.find(t => t.type === 2)?.status ?? 1;
    return {
        spStatus,
        pcStatus
    };
}

/**
 * 初始化数据传递
 */
export function getEditorInitData(params: {
    experience: Experience;
    versionId: string;
    terminalId: string;
    editorType: string;
    locale: string;
    originalURL: string;
}) {
    const collectId = _getCollectId();
    return {
        accountInfo: _parseData(account.value),
        profileInfo: _parseData(profileInfo.value),
        packageInfo: profileInfo.value?.isNpPackage ? getPtxOldPackageInfo() : packageInfo.value,
        newPtePackageInfo: _parseData(packageInfo.value),
        experienceInfo: _parseData(params.experience),
        versionId: params.versionId,
        terminalId: params.terminalId,
        token,
        language: params.locale,
        originalURL: params.originalURL,
        isDemo: false,
        canUseForm: true,
        canPublishTemplate,
        hasForm: false,
        collectId: collectId
            ? {
                  profileId: collectId.profileId,
                  deviceId: collectId.deviceId,
                  sessionId: collectId.sessionId
              }
            : null
    };
}

function _parseData(data: any) {
    if (!data) return null;
    return JSON.parse(JSON.stringify(data));
}

function _getCollectId() {
    const collectIdStr = cookieUtils.getItem('ptx_' + import.meta.env.VITE_COLLECT_ID);
    const collectId = collectIdStr
        ? Object.fromEntries(collectIdStr.split('&').map(s => s.split('=')))
        : null;
    return collectId;
}
